import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Break, Button, Dropdown, Icon, InputText, Picture, Spinner, Text } from "~/components-shared";
import api from "~/lib/api";
import { MembersSortByEnum } from "~/types/enums";
import styled, { useTheme } from "styled-components";
import InfiniteScroll from "react-infinite-scroll-component";
import { handleError } from "~/lib/helpers";
import InputTextControl from "~/components/InputTextControl";
import lists from "~/lib/lists";
const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const StyledIcon = styled(Icon)`
  pointer-events: none;
`;
const StickyTopBox = styled(Box)`
  position: sticky;
  top: 0;
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}};
  z-index: 1;
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const mapMemberToOption = (_ref2, theme) => {
  let {
    id,
    firstName,
    lastName,
    role,
    email,
    type,
    profileImage
  } = _ref2;
  return {
    id,
    showImage: true,
    image: profileImage,
    pre: role?.name,
    value: `${firstName} ${lastName}`,
    accessory: !!type && /*#__PURE__*/React.createElement(Picture, {
      border: "squared",
      src: lists.members.types.logos(theme)[type],
      width: "1.5rem",
      height: "1.5rem",
      backgroundSize: "contain",
      display: "inline-block",
      s: {
        mr: 1,
        mb: -1.5
      }
    }),
    post: email
  };
};
const MemberTeamForm = _ref3 => {
  let {
    control,
    watch,
    setValue
  } = _ref3;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [pagination, setPagination] = useState();
  const [options, setOptions] = useState([]);
  const [error, setError] = useState(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const loadMembers = function () {
    let more = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    setError(undefined);
    api.fetchMembers({
      offset: more ? pagination?.after || undefined : undefined,
      size: 6,
      sort: MembersSortByEnum.BY_ROLE_DESC,
      filter: {
        query: searchQuery
      }
    }).then(response => {
      setOptions(prev => [...(more ? prev : []), ...response.items.map(member => mapMemberToOption(member, theme))]);
      setPagination(response.pagination);
    }).catch(e => {
      setError(handleError(e));
    });
  };
  useEffect(() => {
    loadMembers(false);
  }, [searchQuery]);
  const handleSelect = id => {
    if (watch("memberIds").includes(id)) {
      setValue("memberIds", watch("memberIds").filter(option => option !== id));
    } else {
      setValue("memberIds", [...watch("memberIds"), id]);
    }
  };
  const handleBulkAdd = () => {
    const newValue = options.map(option => option.id);
    setValue("memberIds", [...watch("memberIds"), ...newValue]);
  };
  return /*#__PURE__*/React.createElement(Fieldset, null, /*#__PURE__*/React.createElement(StickyTopBox, {
    gap: 3,
    justify: "end",
    s: {
      p: 4
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputTextControl, {
    autoFocus: true,
    name: "name",
    placeholder: "Team name",
    fullWidth: true,
    control: control
  }), /*#__PURE__*/React.createElement(InputText, {
    value: searchQuery,
    onChange: value => setSearchQuery(value),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: "Search Members",
    elemAfter: /*#__PURE__*/React.createElement(React.Fragment, null, searchQuery && /*#__PURE__*/React.createElement(Button, {
      onClick: handleBulkAdd,
      fontSize: 3,
      color: "transparent",
      s: {
        ml: 1,
        px: 2,
        py: 0.5,
        mr: -1
      },
      outline: false
    }, t("InputSelect.selectFound"))),
    fullWidth: true,
    clearButton: true
  })), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), options.length ? /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: options.length || 0,
    next: () => loadMembers(true),
    hasMore: !!pagination?.hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      s: {
        mt: 6
      },
      center: true
    }),
    scrollableTarget: "member-team-scroll-target"
  }, options.map(option => `${option.id}`.includes("lineBreak") ? /*#__PURE__*/React.createElement(Break, null, option.value && /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, option.value)) : /*#__PURE__*/React.createElement(Dropdown.ListItem, {
    onClick: () => {
      handleSelect(option.id);
    },
    key: option.id,
    selected: option.selected ? true : watch("memberIds").includes(option.id),
    textWrap: true,
    indent: option.indent
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: option.selected || watch("memberIds").includes(option.id) ? "checkboxFill" : "checkbox",
    color: option.disabled ? "disabled" : "primary",
    s: {
      mr: 2
    }
  }), option.showImage && /*#__PURE__*/React.createElement(Picture, {
    name: `${option.value}`,
    src: option.image,
    color: option.imageColor || theme.colors.primary.main,
    width: option.pre || option.post ? "46px" : "32px",
    height: option.pre || option.post ? "46px" : "32px",
    s: {
      mr: 2
    },
    border: "circled"
  }), /*#__PURE__*/React.createElement(Box, {
    fullWidth: true
  }, option.pre && /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.primary.main,
    fontSize: 3,
    weight: "semiBold"
  }, option.pre), option.pre || option.post ? /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "space-between",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    lineHeight: 5,
    weight: "semiBold"
  }, option.value), option.accessory) : /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "space-between"
  }, option.value, option.accessory), option.post && /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 3,
    weight: "semiBold"
  }, option.post)), option.tip && /*#__PURE__*/React.createElement(Icon, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": option.tip,
    src: "infoOutline",
    color: theme.colors.text.secondary,
    s: {
      ml: 1
    }
  })))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    display: "block",
    s: {
      p: 3
    }
  }, "No members found"));
};
export default MemberTeamForm;